import { toAssessmentPath } from "@p0security/shared/assessment/helper";
import { PresetMonitors } from "@p0security/shared/assessment/issues/presets/index";
import { convertSavedMonitor } from "@p0security/shared/assessment/monitor";
import {
  ALL_SCOPE_SENTINEL,
  providerToScope,
} from "@p0security/shared/types/assessment";
import {
  EnvironmentMonitor,
  Monitor,
} from "@p0security/shared/types/assessment/monitor";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { useFirestoreCollection } from "providers/FirestoreProvider";
import React, { createContext, useContext, useMemo } from "react";

import { Tenant } from "../../Login";
import { MonitorWithMetadata } from "./FindingsContext";
import { ScopeContext } from "./ScopeContext";

export interface MonitorsContextValue {
  monitors?: Record<string, MonitorWithMetadata>;
  loading: boolean;
}

export const MonitorsContext = createContext<MonitorsContextValue>({
  monitors: {},
  loading: true,
});

export const MonitorsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const tenantId = useContext(Tenant);
  const { details, last } = useContext(SelectedEnvironmentContext);
  const { provider } = useContext(ScopeContext);

  const assessmentId = details?.assessmentId;
  const assessmentPath = assessmentId
    ? toAssessmentPath(tenantId, assessmentId)
    : undefined;
  const { docs: customMonitors, loading } =
    useFirestoreCollection<EnvironmentMonitor>(
      assessmentPath ? `${assessmentPath}/monitors` : undefined,
      { live: true }
    );

  const monitors = useMemo(() => {
    const assessmentProviders = new Set(
      provider === "all"
        ? [
            ...(last.doc?.data.scope.map(
              (s: { integration: string }) => s.integration
            ) ?? []),
            ALL_SCOPE_SENTINEL,
          ]
        : [providerToScope(provider)]
    );

    const processedCustomMonitors = (customMonitors ?? [])
      .filter((monitor) => {
        if ("show" in monitor.data) {
          return true;
        }
        return decodeURI(monitor.id) in PresetMonitors;
      })
      .map(({ data, id }) => {
        const decodedId = decodeURI(id);
        if ("show" in data) {
          const custom = convertSavedMonitor(data);
          return [
            decodedId,
            {
              ...custom,
              isCustom: true,
            },
          ];
        }
        const preset = PresetMonitors[decodedId as keyof typeof PresetMonitors];
        return [
          decodedId,
          {
            ...preset,
            priority: data.priority ?? preset.priority,
            isCustom: false,
            autoAssign: data.autoAssign ?? false,
            assignee: data.assignee,
            archived: data.archived ?? false,
          },
        ];
      });

    return Object.fromEntries(
      Object.entries({
        ...Object.fromEntries(
          Object.entries(PresetMonitors).map(([id, monitor]) => [
            id,
            { ...monitor, isCustom: false, archived: false },
          ])
        ),
        ...(Object.fromEntries(processedCustomMonitors) as Record<
          string,
          Monitor
        >),
      }).filter(([_k, v]) => v.scopes.some((s) => assessmentProviders.has(s)))
    ) as Record<string, MonitorWithMetadata>;
  }, [provider, last.doc?.data.scope, customMonitors]);
  return (
    <MonitorsContext.Provider
      value={{
        monitors: loading ? undefined : monitors,
        loading,
      }}
    >
      {children}
    </MonitorsContext.Provider>
  );
};

export const useMonitors = () => useContext(MonitorsContext);
